import React, { ReactElement } from 'react';

import { Control } from 'react-hook-form';
import { observer } from 'mobx-react';
import { Button, LbInput, LbSelect, LbDatePicker } from '@infoods/lifebase-ui';

import { ComboBox } from '../../../../../shared/components/form-components';
import {
  CheckboxCell,
  GridTableRow,
} from '../../../../../shared/components/table-components';
import { scholasticCustomersListStore } from '../../../../stores/ScholasticCustomersListStore';
import { scholasticCustomersStatusOptions } from '../../../../constants/scholasticCustomersStatus';
import { ScholasticCommunicationFilterFormValue } from '../../../../interfaces/scholastic-list/ScholasticCommunicationFilters';
import { CustomFiltersSelector } from '../../../../../shared/features/custom-filter';
import { useSchoolsList } from '../../../../../shared/hooks/schools/useSchoolsList';
import {
  SCHOLASTIC_CUSTOMER_COLUMN,
  ScholasticCustomerColumn,
} from '../../NgzScholasticCustomerList.settings';

type GeneralFiltersProps = {
  control: Control<ScholasticCommunicationFilterFormValue>;
  onReset: () => void;
  visibleColumns: ScholasticCustomerColumn[];
};

const GeneralFilters = observer(
  ({ control, onReset, visibleColumns }: GeneralFiltersProps) => {
    const { schoolListOptionWithAll } = useSchoolsList({ isScholastic: true });
    const { selectMethod } = scholasticCustomersListStore;
    const { getMasterCheckboxProps } = selectMethod;

    const inputGeneralProps = {
      control,
      variant: 'outlined',
      size: 'small',
    } as const;

    const renderColumnsInfo: Record<ScholasticCustomerColumn, ReactElement> = {
      [SCHOLASTIC_CUSTOMER_COLUMN.NAME]: (
        <CheckboxCell showCheckbox {...getMasterCheckboxProps()}>
          <LbInput
            {...inputGeneralProps}
            name="generalFilter.fullName"
            placeholder="Name"
            dataTestId="name-input"
          />
        </CheckboxCell>
      ),
      [SCHOLASTIC_CUSTOMER_COLUMN.LOCATION]: (
        <LbInput
          {...inputGeneralProps}
          name="generalFilter.entityName"
          placeholder="Location"
          dataTestId="location-input"
        />
      ),
      [SCHOLASTIC_CUSTOMER_COLUMN.AFFILIATE]: (
        <ComboBox
          {...inputGeneralProps}
          name="generalFilter.schoolId"
          options={schoolListOptionWithAll}
          defaultValue=""
          dataTestId="group-input"
        />
      ),
      [SCHOLASTIC_CUSTOMER_COLUMN.COACH]: (
        <LbInput
          {...inputGeneralProps}
          name="generalFilter.coachName"
          placeholder="Name"
          dataTestId="coach-input"
        />
      ),
      [SCHOLASTIC_CUSTOMER_COLUMN.START_DATE]: (
        <LbDatePicker {...inputGeneralProps} name="generalFilter.validFrom" />
      ),
      [SCHOLASTIC_CUSTOMER_COLUMN.STATUS]: (
        <LbSelect
          {...inputGeneralProps}
          name="generalFilter.status"
          options={scholasticCustomersStatusOptions}
          displayEmpty
          dataTestId="status-input"
        />
      ),
      [SCHOLASTIC_CUSTOMER_COLUMN.CUSTOM_FILTER]: (
        <CustomFiltersSelector {...inputGeneralProps} name="customFilterId" />
      ),
      [SCHOLASTIC_CUSTOMER_COLUMN.ACTIONS]: (
        <Button variant="outlined" size="small" type="reset" fullWidth onClick={onReset}>
          Clear All
        </Button>
      ),
    };

    return (
      <GridTableRow variant="filter" component="form">
        {visibleColumns.map((col) => (
          <React.Fragment key={col}>{renderColumnsInfo[col]}</React.Fragment>
        ))}
      </GridTableRow>
    );
  },
);

export { GeneralFilters };
